"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applySiteBrandingToFaceTec = void 0;
var store_1 = require("../../../app/store");
var FaceTecSDK_1 = require("./../../../core-sdk/FaceTecSDK.js/FaceTecSDK");
// Fallback palette when no site config is present in Redux. Dupwies is
// the Lewfin default tenant — red primary, matching the Lewfin brand
// (and the `bg-red-600` used on the KYC "Verify Identity" button).
var LEWFIN_FALLBACK = {
    primaryColor: "#dc2626", // Lewfin red (tailwind red-600)
    secondaryColor: "#b91c1c", // darker red (tailwind red-700)
    logo: "/branding/Dupwies.png",
};
// Chrome around the capture area is always neutral. Site `accentColor`
// from branding is intended for CTA emphasis (e.g. "Get Started" buttons
// on the marketing page), not for filling the FaceTec overlay. Testing
// with Lighthouse Finance showed amber `#F59E0B` flooding the whole
// "Securing Camera Feed" screen — clearly wrong. Keep it white; primary
// colour handles the interactive elements.
var OVERLAY_BACKGROUND = "#ffffff";
var DISABLED_BUTTON_COLOR = "#B9CCDE";
// Pull branding from the current site in Redux, falling back to Lewfin
// defaults if no site has been loaded yet. Defensive by design — the
// FaceTec controller runs outside the React tree, so any of these fields
// could be absent or malformed on a cold-boot edge case.
var resolveBranding = function () {
    var _a, _b, _c, _d, _e;
    var site = (_b = (_a = store_1.store.getState().site) === null || _a === void 0 ? void 0 : _a.current) !== null && _b !== void 0 ? _b : null;
    var b = site === null || site === void 0 ? void 0 : site.branding;
    return {
        primaryColor: ((_c = b === null || b === void 0 ? void 0 : b.primaryColor) === null || _c === void 0 ? void 0 : _c.trim()) || LEWFIN_FALLBACK.primaryColor,
        secondaryColor: ((_d = b === null || b === void 0 ? void 0 : b.secondaryColor) === null || _d === void 0 ? void 0 : _d.trim()) || LEWFIN_FALLBACK.secondaryColor,
        logo: ((_e = b === null || b === void 0 ? void 0 : b.logo) === null || _e === void 0 ? void 0 : _e.trim()) || LEWFIN_FALLBACK.logo,
    };
};
// Build a fully-populated FaceTecCustomization driven by a tenant's
// branding colours + logo. Touches every customization slot that would
// otherwise inherit a hardcoded Sky Lifestyle value from the Config Wizard
// preset — see app/Config.js:96-227 for the equivalent static mapping.
var buildCustomization = function (branding) {
    var customization = new FaceTecSDK_1.FaceTecSDK.FaceTecCustomization();
    var primaryColor = branding.primaryColor, secondaryColor = branding.secondaryColor, logo = branding.logo;
    // Frame
    customization.frameCustomization.borderCornerRadius = "20px";
    customization.frameCustomization.backgroundColor = OVERLAY_BACKGROUND;
    customization.frameCustomization.borderColor = primaryColor;
    // Overlay (outer chrome + logo placement)
    customization.overlayCustomization.brandingImage = logo;
    customization.overlayCustomization.backgroundColor = OVERLAY_BACKGROUND;
    // Guidance (instructions + button states)
    customization.guidanceCustomization.backgroundColors = OVERLAY_BACKGROUND;
    customization.guidanceCustomization.foregroundColor = primaryColor;
    customization.guidanceCustomization.buttonBackgroundNormalColor = primaryColor;
    customization.guidanceCustomization.buttonBackgroundDisabledColor = DISABLED_BUTTON_COLOR;
    customization.guidanceCustomization.buttonBackgroundHighlightColor = secondaryColor;
    customization.guidanceCustomization.buttonTextNormalColor = "#ffffff";
    customization.guidanceCustomization.buttonTextDisabledColor = "#ffffff";
    customization.guidanceCustomization.buttonTextHighlightColor = "#ffffff";
    customization.guidanceCustomization.retryScreenImageBorderColor = primaryColor;
    customization.guidanceCustomization.retryScreenOvalStrokeColor = primaryColor;
    // Oval (face-detection oval)
    customization.ovalCustomization.strokeColor = primaryColor;
    customization.ovalCustomization.progressColor1 = secondaryColor;
    customization.ovalCustomization.progressColor2 = secondaryColor;
    // Feedback (status bar during session)
    customization.feedbackCustomization.backgroundColor = primaryColor;
    customization.feedbackCustomization.textColor = "#ffffff";
    // Cancel button
    customization.cancelButtonCustomization.location =
        FaceTecSDK_1.FaceTecSDK.FaceTecCancelButtonLocation.TopLeft;
    // Security watermark
    customization.securityWatermarkCustomization.setSecurityWatermarkImage(FaceTecSDK_1.FaceTecSDK.FaceTecSecurityWatermarkImage.FaceTec_ZoOm);
    // Result screen (success/failure animation colours)
    customization.resultScreenCustomization.backgroundColors = OVERLAY_BACKGROUND;
    customization.resultScreenCustomization.foregroundColor = primaryColor;
    customization.resultScreenCustomization.activityIndicatorColor = primaryColor;
    customization.resultScreenCustomization.resultAnimationBackgroundColor = primaryColor;
    customization.resultScreenCustomization.resultAnimationForegroundColor = "#ffffff";
    customization.resultScreenCustomization.uploadProgressFillColor = primaryColor;
    // ID scan (photo-ID capture / review screens)
    customization.idScanCustomization.selectionScreenBackgroundColors = OVERLAY_BACKGROUND;
    customization.idScanCustomization.selectionScreenForegroundColor = primaryColor;
    customization.idScanCustomization.reviewScreenBackgroundColors = OVERLAY_BACKGROUND;
    customization.idScanCustomization.reviewScreenForegroundColor = "#ffffff";
    customization.idScanCustomization.reviewScreenTextBackgroundColor = primaryColor;
    customization.idScanCustomization.captureScreenForegroundColor = "#ffffff";
    customization.idScanCustomization.captureScreenTextBackgroundColor = primaryColor;
    customization.idScanCustomization.buttonBackgroundNormalColor = primaryColor;
    customization.idScanCustomization.buttonBackgroundDisabledColor = DISABLED_BUTTON_COLOR;
    customization.idScanCustomization.buttonBackgroundHighlightColor = secondaryColor;
    customization.idScanCustomization.buttonTextNormalColor = "#ffffff";
    customization.idScanCustomization.buttonTextDisabledColor = "#ffffff";
    customization.idScanCustomization.buttonTextHighlightColor = "#ffffff";
    customization.idScanCustomization.captureScreenBackgroundColor = OVERLAY_BACKGROUND;
    customization.idScanCustomization.captureFrameStrokeColor = primaryColor;
    // Initial loading spinner (before first session frame)
    customization.initialLoadingAnimationCustomization.backgroundColor = OVERLAY_BACKGROUND;
    customization.initialLoadingAnimationCustomization.foregroundColor = primaryColor;
    return customization;
};
// Apply the current site's branding to the FaceTec SDK. Safe to call
// multiple times — the SDK accepts re-customization at any point and
// picks it up for the next session render. Call this at the top of
// each entry point in sampleAppController (onPhotoIDMatchPressed,
// onEnrollUserPressed, etc.) so sessions always carry the branding of
// whichever tenant the user is currently on, regardless of what any
// previous session set.
var applySiteBrandingToFaceTec = function () {
    var branding = resolveBranding();
    var customization = buildCustomization(branding);
    FaceTecSDK_1.FaceTecSDK.setCustomization(customization);
    FaceTecSDK_1.FaceTecSDK.setLowLightCustomization(customization);
    FaceTecSDK_1.FaceTecSDK.setDynamicDimmingCustomization(customization);
};
exports.applySiteBrandingToFaceTec = applySiteBrandingToFaceTec;
